/**
 * The industries a market is segmented into, and the subcategories filed under each.
 *
 * **These are GICS** — the Global Industry Classification Standard, the scheme actually used to
 * segment markets in finance. Its top level is 11 *sectors*, which are our industries; its second
 * level is 25 *industry groups*, which are our seeded categories (the app calls a product's own
 * one its **subcategory**).
 *
 * It replaces ten invented industries ("Finance & Fintech", "Lifestyle & Consumer", …) and, with
 * them, ~320 lines of per-industry demand signals, supply features, keyword profiles and Saudi gap
 * profiles that carried the note *"Verbatim from the design reference; replaced by live research
 * once the backend lands"*. That backend is what ECONOMY-21 builds, so the placeholder is gone
 * rather than left on screen: an unsourced claim an analyst cannot tell apart from a fabricated
 * one is the defect this issue exists to remove.
 *
 * **In contracts, not in the app**, because three processes need the same list and services may
 * not import one another: the web app renders it, the API's taxonomy overlay resolves against it,
 * and the agent-runner's extractor is told to place a product in exactly one of these names or
 * answer null. A second copy would drift the first time the standard is revised.
 *
 * Three reasons the standard beats the list it replaces:
 *
 * 1. **It is not ours.** A reader can check what "Consumer Staples" means without asking us.
 * 2. **It is complete.** Every product has somewhere to go, so nothing is filed by a fallback rule
 *    that really means "we ran out of rules".
 * 3. **Its empty sectors are honest.** Utilities and Real Estate stay empty on a consumer-AI radar,
 *    and an empty sector is information. Padding it would not be.
 */

/**
 * An industry id. Open, not a closed union (ECONOMY-16 / ADR 0107): the eleven below are seeded in
 * code, but the analyst can add their own, so nothing may assume the set is fixed.
 */
export type IndustryKey = string;

/** The eleven GICS sectors that ship with the app. */
export type SeededIndustryKey =
  | 'energy'
  | 'materials'
  | 'industrials'
  | 'consumer-discretionary'
  | 'consumer-staples'
  | 'health-care'
  | 'financials'
  | 'information-technology'
  | 'communication-services'
  | 'utilities'
  | 'real-estate';

/** GICS sector order — the order the standard itself lists them in. */
export const INDUSTRY_ORDER: readonly SeededIndustryKey[] = [
  'energy',
  'materials',
  'industrials',
  'consumer-discretionary',
  'consumer-staples',
  'health-care',
  'financials',
  'information-technology',
  'communication-services',
  'utilities',
  'real-estate',
];

export const INDUSTRY_LABEL: Readonly<Record<SeededIndustryKey, string>> = {
  energy: 'Energy',
  materials: 'Materials',
  industrials: 'Industrials',
  'consumer-discretionary': 'Consumer Discretionary',
  'consumer-staples': 'Consumer Staples',
  'health-care': 'Health Care',
  financials: 'Financials',
  'information-technology': 'Information Technology',
  'communication-services': 'Communication Services',
  utilities: 'Utilities',
  'real-estate': 'Real Estate',
};

/**
 * The GICS industry groups, each under its sector — the app's seeded **categories**, and the set a
 * product's `subcategory` is drawn from.
 *
 * Ordered, because this array is also the order the taxonomy screen and the category pickers offer
 * them in, and a standard read out of its own order reads as a different list.
 */
export const SEEDED_CATEGORIES: readonly (readonly [string, SeededIndustryKey])[] = [
  ['Energy', 'energy'],
  ['Materials', 'materials'],
  ['Capital Goods', 'industrials'],
  ['Commercial & Professional Services', 'industrials'],
  ['Transportation', 'industrials'],
  ['Automobiles & Components', 'consumer-discretionary'],
  ['Consumer Durables & Apparel', 'consumer-discretionary'],
  ['Consumer Services', 'consumer-discretionary'],
  ['Consumer Discretionary Distribution & Retail', 'consumer-discretionary'],
  ['Consumer Staples Distribution & Retail', 'consumer-staples'],
  ['Food, Beverage & Tobacco', 'consumer-staples'],
  ['Household & Personal Products', 'consumer-staples'],
  ['Health Care Equipment & Services', 'health-care'],
  ['Pharmaceuticals, Biotechnology & Life Sciences', 'health-care'],
  ['Banks', 'financials'],
  ['Financial Services', 'financials'],
  ['Insurance', 'financials'],
  ['Software & Services', 'information-technology'],
  ['Technology Hardware & Equipment', 'information-technology'],
  ['Semiconductors & Semiconductor Equipment', 'information-technology'],
  ['Telecommunication Services', 'communication-services'],
  ['Media & Entertainment', 'communication-services'],
  ['Utilities', 'utilities'],
  ['Equity REITs', 'real-estate'],
  ['Real Estate Management & Development', 'real-estate'],
];

/** Seeded category → its GICS sector. Built from `SEEDED_CATEGORIES` so the two cannot disagree. */
export const SEEDED_CATEGORY_INDUSTRY: Readonly<Record<string, SeededIndustryKey>> = Object.fromEntries(
  SEEDED_CATEGORIES.map(([category, industry]) => [category, industry]),
);

/** The seeded category names, in GICS order. */
export const SEEDED_CATEGORY_NAMES: readonly string[] = SEEDED_CATEGORIES.map(([category]) => category);

/**
 * The category a product sits in when nothing has said which — *not* an industry, and not a guess.
 *
 * It exists so "nobody has filed this yet" is a state the screen can name. The code it replaces had
 * no such state: an industry string matching none of 38 keyword rules fell into whichever bucket
 * the last rule happened to reach, and the analyst read a confident answer where there was none.
 */
export const UNFILED_CATEGORY = 'Unfiled';

/**
 * Which GICS sector a category sits under, or `null` when nothing says.
 *
 * `null` is the point. This used to be a keyword table over free text that ALWAYS answered — an
 * unrecognised industry became `lifestyle`, indistinguishable on screen from one the rules had
 * genuinely recognised. A category the standard does not name, and the analyst has not filed, has
 * no sector, and saying so is the only honest answer available.
 */
export function industryOf(category: string): SeededIndustryKey | null {
  return SEEDED_CATEGORY_INDUSTRY[category.trim()] ?? null;
}
